/**
 * Shared instruction-message definitions for the `return-shipping-instructions`
 * block, used by both the email renderer (to pick the right message) and the
 * config panel (to list the editable messages in a dropdown).
 *
 * There are 9 distinct messages; several carrier codes map to the same message
 * (e.g. all UPS variants → `ups`), mirroring the legacy
 * `return-request-label-created` email `descriptionsMap`.
 */

export type InstructionKey =
  | "colissimo"
  | "chronoShop2Shop"
  | "ups"
  | "customerCarrier"
  | "merchantCarrier"
  | "returnToStore"
  | "malca"
  | "tnt"
  | "bonDeRetour";

export type InstructionDefinition = {
  id: InstructionKey;
  /** Key in the block `tLayout` dictionary (`shippingInstructions.*`). */
  tLayoutKey: string;
  /** English fallback (tLayout has no per-key fallback). */
  fallback: string;
  /** i18n key for the dropdown option label (editor-blocks-library namespace). */
  labelKey: string;
  /** Fallback label for the dropdown option. */
  labelFallback: string;
};

const LABEL_KEY = (id: string) =>
  `editorBlocksLibrary.blocks.return-shipping-instructions.panel.message.${id}`;

export const INSTRUCTION_KEYS: InstructionDefinition[] = [
  {
    id: "colissimo",
    tLayoutKey: "shippingInstructions.colissimo",
    fallback:
      "You can now print your return label and drop off your package at the nearest Colissimo relay point.",
    labelKey: LABEL_KEY("colissimo"),
    labelFallback: "Colissimo",
  },
  {
    id: "chronoShop2Shop",
    tLayoutKey: "shippingInstructions.chronoShop2Shop",
    fallback:
      "You can now print your return label and drop off your package at the nearest Chronopost relay point.",
    labelKey: LABEL_KEY("chronoShop2Shop"),
    labelFallback: "Chronopost / pickup point",
  },
  {
    id: "ups",
    tLayoutKey: "shippingInstructions.ups",
    fallback:
      "You can now print your return label and drop off your package at the nearest UPS relay point.",
    labelKey: LABEL_KEY("ups"),
    labelFallback: "UPS",
  },
  {
    id: "customerCarrier",
    tLayoutKey: "shippingInstructions.customerCarrier",
    fallback: "You can now send your return package to the carrier of your choice.",
    labelKey: LABEL_KEY("customerCarrier"),
    labelFallback: "Customer's carrier",
  },
  {
    id: "merchantCarrier",
    tLayoutKey: "shippingInstructions.merchantCarrier",
    fallback: "We will send you a return label by email within 1 to 3 business days.",
    labelKey: LABEL_KEY("merchantCarrier"),
    labelFallback: "Merchant's carrier",
  },
  {
    id: "returnToStore",
    tLayoutKey: "shippingInstructions.returnToStore",
    fallback:
      "You can now print your return label and drop off your package at the nearest store.",
    labelKey: LABEL_KEY("returnToStore"),
    labelFallback: "Return to store",
  },
  {
    id: "malca",
    tLayoutKey: "shippingInstructions.malca",
    fallback:
      "You can now print your return label and drop off your package at the nearest Malca relay point.",
    labelKey: LABEL_KEY("malca"),
    labelFallback: "Malca",
  },
  {
    id: "tnt",
    tLayoutKey: "shippingInstructions.tnt",
    fallback:
      "You can now print your return label and drop off your package at the nearest TNT relay point.",
    labelKey: LABEL_KEY("tnt"),
    labelFallback: "TNT",
  },
  {
    id: "bonDeRetour",
    tLayoutKey: "shippingInstructions.bonDeRetour",
    fallback:
      "Your return slip is ready. You can now print it and use it for your return.",
    labelKey: LABEL_KEY("bonDeRetour"),
    labelFallback: "Return slip",
  },
];

export const INSTRUCTION_BY_ID: Record<InstructionKey, InstructionDefinition> =
  INSTRUCTION_KEYS.reduce(
    (acc, def) => {
      acc[def.id] = def;
      return acc;
    },
    {} as Record<InstructionKey, InstructionDefinition>,
  );

/** Maps a raw carrier code to its instruction message. */
const CARRIER_TO_INSTRUCTION: Record<string, InstructionKey> = {
  colissimo: "colissimo",
  "chrono-shop2shop": "chronoShop2Shop",
  crosslog: "chronoShop2Shop",
  "ups-saver": "ups",
  "ups-standard": "ups",
  "ups-express": "ups",
  "fake-dpd": "ups",
  "customer-carrier": "customerCarrier",
  "merchant-carrier": "merchantCarrier",
  "return-to-store": "returnToStore",
  malca: "malca",
  tnt: "tnt",
};

/**
 * True when an HTML override is "visually empty" — the rich-text editor emits
 * `<p></p>` (not `""`) when cleared, which must be treated as "no override" so
 * the default message is used.
 */
export function isEmptyHtml(html?: string | null): boolean {
  if (!html) return true;
  const stripped = html
    .replace(/<br\s*\/?>/gi, "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();
  return stripped.length === 0;
}

/**
 * Selection logic mirroring the legacy email: a logistic return slip overrides
 * everything; otherwise pick by carrier code, falling back to the "customer
 * carrier" copy for unknown / missing carriers (also the editor preview case).
 */
export function resolveInstructionKey(
  carrierCode?: string,
  hasLogisticLabel?: boolean,
): InstructionKey {
  if (hasLogisticLabel) return "bonDeRetour";
  return (carrierCode && CARRIER_TO_INSTRUCTION[carrierCode]) || "customerCarrier";
}
